<template>
  <div class="array-item-editor space-y-3">
    <div v-for="[fieldName, fieldDef] in Object.entries(itemSchema)" :key="fieldName" class="field">
      <!-- String Input -->
      <div v-if="fieldDef.type === 'string'">
        <label class="block text-xs font-medium text-gray-600 mb-1">{{ fieldDef.label }}</label>
        <InputText
          :model-value="item[fieldName] || ''"
          class="w-full text-sm"
          :placeholder="`Enter ${fieldDef.label.toLowerCase()}...`"
          @input="updateField(fieldName, ($event.target as HTMLInputElement).value)"
        />
      </div>

      <!-- HTML content field, edited as PLAIN TEXT (D2): owner sees paragraphs, not
           <p> markup. Strip on load, re-wrap to HTML on save — store stays HTML so the
           accordion renderer (v-html) is unchanged. -->
      <div v-else-if="fieldDef.type === 'textarea' && fieldDef.html">
        <label class="block text-xs font-medium text-gray-600 mb-1">{{ fieldDef.label }}</label>
        <Textarea
          :model-value="htmlDrafts[fieldName] ?? ''"
          class="w-full text-sm"
          rows="3"
          :placeholder="`Enter ${fieldDef.label.toLowerCase()}...`"
          @input="updateHtmlField(fieldName, ($event.target as HTMLTextAreaElement).value)"
        />
      </div>

      <!-- Textarea Input (plain text) -->
      <div v-else-if="fieldDef.type === 'textarea'">
        <label class="block text-xs font-medium text-gray-600 mb-1">{{ fieldDef.label }}</label>
        <Textarea
          :model-value="item[fieldName] || ''"
          class="w-full text-sm"
          rows="3"
          :placeholder="`Enter ${fieldDef.label.toLowerCase()}...`"
          @input="updateField(fieldName, ($event.target as HTMLTextAreaElement).value)"
        />
      </div>

      <!-- Select Input -->
      <div v-else-if="fieldDef.type === 'select'">
        <label class="block text-xs font-medium text-gray-600 mb-1">{{ fieldDef.label }}</label>
        <Select
          :model-value="item[fieldName] || (fieldDef.options ? fieldDef.options[0] : '')"
          :options="fieldDef.options?.map((opt: string) => ({ label: opt.charAt(0).toUpperCase() + opt.slice(1), value: opt }))"
          option-label="label"
          option-value="value"
          class="w-full text-sm"
          @change="updateField(fieldName, $event.value)"
        />
      </div>

      <!-- Number Input -->
      <div v-else-if="fieldDef.type === 'number'">
        <label class="block text-xs font-medium text-gray-600 mb-1">{{ fieldDef.label }}</label>
        <InputNumber
          :model-value="item[fieldName] || 0"
          class="w-full text-sm"
          :placeholder="`Enter ${fieldDef.label.toLowerCase()}...`"
          @input="updateField(fieldName, $event.value)"
        />
      </div>

      <!-- Boolean Input -->
      <div v-else-if="fieldDef.type === 'boolean'">
        <div class="flex items-center space-x-2">
          <Checkbox
            :model-value="item[fieldName] || false"
            binary
            @change="updateField(fieldName, $event)"
          />
          <label class="text-xs font-medium text-gray-600">{{ fieldDef.label }}</label>
        </div>
      </div>

      <!-- URL Input -->
      <div v-else-if="fieldDef.type === 'url'">
        <label class="block text-xs font-medium text-gray-600 mb-1">{{ fieldDef.label }}</label>
        <InputText
          :model-value="item[fieldName] || ''"
          class="w-full text-sm"
          type="url"
          :placeholder="`Enter ${fieldDef.label.toLowerCase()}...`"
          @input="updateField(fieldName, ($event.target as HTMLInputElement).value)"
        />
      </div>

      <!-- Color Input -->
      <div v-else-if="fieldDef.type === 'color'">
        <label class="block text-xs font-medium text-gray-600 mb-1">{{ fieldDef.label }}</label>
        <div class="flex items-center space-x-2">
          <ColorPicker
            :model-value="(item[fieldName] || '#ffffff').replace(/^#/, '')"
            @update:modelValue="updateField(fieldName, '#' + $event)"
          />
          <InputText
            :model-value="item[fieldName] || '#ffffff'"
            class="flex-1 text-sm"
            @input="updateField(fieldName, ($event.target as HTMLInputElement).value)"
          />
        </div>
      </div>

      <!-- Image Field -->
      <div v-else-if="fieldDef.type === 'image'">
        <ImageField
          :model-value="item[fieldName] || null"
          :label="fieldDef.label"
          :project-id="projectId"
          @update:modelValue="updateField(fieldName, $event)"
        />
      </div>

      <!-- String list (string[]), edited one value per line. Committed on change (blur)
           so pressing Enter isn't swallowed by the blank-line filter mid-typing. -->
      <div v-else-if="fieldDef.type === 'stringList'">
        <label class="block text-xs font-medium text-gray-600 mb-1">{{ fieldDef.label }}</label>
        <Textarea
          :model-value="stringListToText(item[fieldName])"
          class="w-full text-sm"
          rows="4"
          placeholder="One per line..."
          @change="updateField(fieldName, textToStringList(($event.target as HTMLTextAreaElement).value))"
        />
      </div>

      <!-- Nested array (e.g. menu category → items): recursive ArrayField -->
      <div v-else-if="fieldDef.type === 'array' && fieldDef.itemSchema" class="pl-2 border-l-2 border-gray-200">
        <ArrayField
          :model-value="item[fieldName] || []"
          :label="fieldDef.label"
          :item-schema="getArrayItemSchemaByName(fieldDef.itemSchema)"
          :project-id="projectId"
          @update:modelValue="updateField(fieldName, $event)"
        />
      </div>

      <!-- Fallback for unknown field types -->
      <div v-else>
        <label class="block text-xs font-medium text-gray-600 mb-1">{{ fieldDef.label }} ({{ fieldDef.type }})</label>
        <div class="text-xs text-gray-500 p-2 bg-gray-100 rounded border">
          Field type "{{ fieldDef.type }}" not implemented
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch } from 'vue'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Select from 'primevue/select'
import InputNumber from 'primevue/inputnumber'
import Checkbox from 'primevue/checkbox'
import ColorPicker from 'primevue/colorpicker'
import ImageField from './ImageField.vue'
// Recursive: ArrayField renders ArrayItemEditor, which renders ArrayField for nested arrays.
// The circular import is safe — components are only dereferenced at render time.
import ArrayField from './ArrayField.vue'
import { getArrayItemSchemaByName } from '@/schemas/componentSchemas'
import { stringListToText, textToStringList } from '@/utils/editorHelpers'
import { useHtmlDraft } from '@/composables/useHtmlDraft'

interface Props {
  item: any
  itemSchema: Record<string, any>
  projectId: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  update: [updatedFields: any]
}>()

// Update a specific field in the item
const updateField = (fieldName: string, value: any) => {
  emit('update', { [fieldName]: value })
}

// D2 plain-text editing for html-flagged textarea fields (shared composable). The
// parent rebuilds the item on every keystroke, so the composable's lastEmitted guard
// re-seeds the draft only on an EXTERNAL change (reorder / a different item bound here),
// never from our own input — which is what keeps the cursor from jumping.
const { htmlDrafts, seed, write } = useHtmlDraft()
watch(
  () => props.item,
  (item) => {
    if (!item) return
    seed(
      Object.entries(props.itemSchema)
        .filter(([, def]) => def.type === 'textarea' && def.html)
        .map(([name]) => [name, item[name] ?? '']),
    )
  },
  { immediate: true },
)

// Store stays HTML so the accordion v-html renderer is unchanged.
function updateHtmlField(fieldName: string, plain: string) {
  write(fieldName, plain, (html) => updateField(fieldName, html))
}
</script>